import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Box, Loader, Text } from '@mantine/core';
import config from '../utils/config';

// Testing flag to control console logging
const TESTING = false;

// Global cache shared across all instances
const globalAvatarCache: { [key: string]: { data: any; timestamp: number } } = {};
const globalLoadingState: { [key: string]: boolean } = {};

interface RobloxAvatar3DProps {
  userId: string;
  width?: number;
  height?: number;
  backgroundColor?: string;
  showLoading?: boolean;
  autoRotate?: boolean;
  rotationSpeed?: number;
  viewMode?: 'headshot' | 'full';
  onLoad?: () => void;
  onError?: (error: string) => void;
  className?: string;
  style?: React.CSSProperties;
  scrollRotation?: boolean;
  scrollFrameRate?: number; // FPS for scroll rotation updates
}

interface Avatar3DData {
  state: string;
  ready: boolean;
  message?: string;
  metadata: any;
  objUrl: string;
  mtlUrl: string;
  textureUrls: string[];
}

export const RobloxAvatar3D: React.FC<RobloxAvatar3DProps> = ({
  userId,
  width = 300,
  height = 300,
  backgroundColor = '#404040',
  showLoading = true,
  autoRotate = true,
  rotationSpeed = 0.0025,
  viewMode = 'full',
  onLoad,
  onError,
  className,
  style,
  scrollRotation = false,
  scrollFrameRate = 30
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [avatarData, setAvatarData] = useState<Avatar3DData | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const sceneRef = useRef<{
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    renderer: THREE.WebGLRenderer;
    controls?: OrbitControls;
    animationId?: number;
    model?: THREE.Object3D;
  } | null>(null);
  
  // Simple cache to prevent duplicate API calls
  const avatarCache = useRef<{ [key: string]: { data: Avatar3DData; timestamp: number } }>({});
  const loadingRef = useRef<{ [key: string]: boolean }>({});

  // CDN URL generator function from the Roblox writeup
  const getCdnUrl = useCallback((hash: string): string => {
    let i = 31;
    for (let t = 0; t < 38; t++) {
      i ^= hash[t].charCodeAt(0);
    }
    return `https://t${(i % 8).toString()}.rbxcdn.com/${hash}`;
  }, []);

  // Fetch avatar data from our backend API
  const fetchAvatarData = useCallback(async (userId: string): Promise<Avatar3DData> => {
    // Check global cache first (5 minute cache)
    const cacheKey = `avatar_${userId}`;
    const cached = globalAvatarCache[cacheKey];
    if (cached && Date.now() - cached.timestamp < 300000) {
      return cached.data;
    }
    
    // Check local cache as fallback
    const localCached = avatarCache.current[cacheKey];
    if (localCached && Date.now() - localCached.timestamp < 300000) {
      return localCached.data;
    }
    
    // If a request is already in progress, wait for it to complete
    if (globalLoadingState[userId] || loadingRef.current[userId]) {
      // Wait for the existing request to complete
      let attempts = 0;
      while ((globalLoadingState[userId] || loadingRef.current[userId]) && attempts < 50) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      // Check if we got cached data while waiting
      const cached = globalAvatarCache[cacheKey];
      if (cached && Date.now() - cached.timestamp < 300000) {
        return cached.data;
      }
      
      const localCached = avatarCache.current[cacheKey];
      if (localCached && Date.now() - localCached.timestamp < 300000) {
        return localCached.data;
      }
    }
    
    globalLoadingState[userId] = true;
    loadingRef.current[userId] = true;
    
    try {
      const response = await fetch(`${config.flaskApiUrl}/api/v1/avatar/${userId}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`API error: ${response.status} - ${errorData.error || 'Unknown error'}`);
      }
      
      const data = await response.json();
      
      if (!data.ready) {
        throw new Error(data.message || 'Avatar not ready');
      }
      
      // Cache the result in both global and local cache
      globalAvatarCache[cacheKey] = { data, timestamp: Date.now() };
      avatarCache.current[cacheKey] = { data, timestamp: Date.now() };
      
      return data;
    } finally {
      globalLoadingState[userId] = false;
      loadingRef.current[userId] = false;
    }
  }, []);

  // Setup Three.js scene
  const setupScene = useCallback(() => {
    if (!containerRef.current) return null;

    const container = containerRef.current;
    container.innerHTML = ''; // Clear previous content

    // Create scene
    const scene = new THREE.Scene();
    // Remove background to make it transparent
    scene.background = null;

    // Create camera with positioning based on view mode
    const camera = new THREE.PerspectiveCamera(
      viewMode === 'headshot' ? 35 : 35, // Keep consistent FOV
      width / height,
      0.1,
      1000
    );
    
    // Position camera based on view mode
    if (viewMode === 'headshot') {
      // Closer and more level for headshot view
      camera.position.set(2, 0.8, 3);
      camera.lookAt(0, 0, 0); // Look more level at head area
    } else {
      // Position camera to frame model better in rectangular viewport
      camera.position.set(4, 1.5, 6);
      camera.lookAt(0, 0.5, 0); // Look slightly higher to center model in frame
    }

    // Create renderer with alpha support for transparency
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0); // Transparent background
    container.appendChild(renderer.domElement);

    // Add comprehensive lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
    scene.add(ambientLight);

    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 1.0);
    directionalLight1.position.set(10, 10, 5);
    scene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight2.position.set(-10, 5, -5);
    scene.add(directionalLight2);

    // Add OrbitControls for non-headshot views (disabled for scroll rotation)
    let controls: OrbitControls | undefined;
    if (viewMode === 'full' && !scrollRotation) {
      controls = new OrbitControls(camera, renderer.domElement);
      
      // Configure controls for better user experience
      controls.enableDamping = true; // Smooth camera movements
      controls.dampingFactor = 0.05;
      controls.screenSpacePanning = false;
      
      // Set reasonable zoom limits
      controls.minDistance = 2;
      controls.maxDistance = 15;
      
      // Set rotation limits (prevent flipping upside down)
      controls.maxPolarAngle = Math.PI;
      
      // Set the target to look at the center
      controls.target.set(0, 0, 0);
      controls.update();
      
      TESTING && console.log('OrbitControls enabled for full body view');
    }

    TESTING && console.log(`Scene setup complete for ${viewMode} view`);
    TESTING && console.log('Camera position:', camera.position);
    TESTING && console.log('Camera looking at:', viewMode === 'headshot' ? '(0, 0, 0)' : '(0, 0, 0)');

    return { scene, camera, renderer, controls };
  }, [width, height, viewMode, scrollRotation]);

  // Scroll rotation effect - set up after model loads
  useEffect(() => {
    if (!scrollRotation || !avatarData) return;

    let lastUpdate = 0;
    const frameInterval = 1000 / scrollFrameRate; // ms between updates

    const handleScroll = () => {
      const now = Date.now();
      if (now - lastUpdate < frameInterval) return; // Throttle updates
      
      lastUpdate = now;

      if (sceneRef.current?.model && sceneRef.current?.renderer && sceneRef.current?.camera) {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
        const scrollPercent = documentHeight > 0 ? scrollTop / documentHeight : 0;
        
        // Start facing forward (Math.PI) and add 4 full rotations based on scroll
        sceneRef.current.model.rotation.y = Math.PI + (scrollPercent * Math.PI * 4);
        
        // Re-render the scene
        sceneRef.current.renderer.render(sceneRef.current.scene, sceneRef.current.camera);
        
        // Debug logging (remove in production)
        // console.log(`Scroll: ${scrollPercent.toFixed(2)}, Rotation: ${sceneRef.current.model.rotation.y.toFixed(2)}`);
      }
    };

    // Set up the scroll listener with a small delay to ensure model is ready
    const timeoutId = setTimeout(() => {
      handleScroll(); // Initial position
      window.addEventListener('scroll', handleScroll, { passive: true });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollRotation, avatarData]); // Depend on avatarData instead of model directly

  // Load the 3D model
  const loadModel = useCallback(async (sceneData: { scene: THREE.Scene; camera: THREE.PerspectiveCamera; renderer: THREE.WebGLRenderer }, avatarData: Avatar3DData) => {
    const { scene, camera, renderer } = sceneData;

    // Load MTL first
    const mtlLoader = new MTLLoader();
    
    // Set up URL modifier for MTL loader to handle texture URLs correctly
    mtlLoader.manager.setURLModifier((url: string) => {
      const id = url.split('com/')[1];
      if (id) {
        return getCdnUrl(id);
      }
      return url;
    });
    
    return new Promise<THREE.Object3D>((resolve, reject) => {
      mtlLoader.load(
        avatarData.mtlUrl,
        (materials: any) => {
          // Disable transparency on all materials
          for (const key in materials.materials) {
            materials.materials[key].transparent = false;
          }
          materials.preload();

          // Load OBJ with materials
          const objLoader = new OBJLoader();
          objLoader.setMaterials(materials);

          // Set up URL modifier for OBJ loader
          objLoader.manager.setURLModifier((url: string) => {
            const id = url.split('com/')[1];
            if (id) {
              return getCdnUrl(id);
            }
            return url;
          });

          objLoader.load(
            avatarData.objUrl,
            (object: any) => {
              // Enhanced positioning for users with large accessories
              TESTING && console.log('Processing avatar model for normalized positioning...');
              
              // First, identify the main body parts vs accessories
              const bodyParts: THREE.Mesh[] = [];
              const accessories: THREE.Mesh[] = [];
              
              object.traverse((child: any) => {
                if (child instanceof THREE.Mesh) {
                  // Typical Roblox body part names start with "Player" followed by a number
                  // Accessories typically have different naming patterns
                  const name = child.name.toLowerCase();
                  if (name.startsWith('player') && /player\d+/.test(name)) {
                    bodyParts.push(child);
                  } else {
                    accessories.push(child);
                  }
                }
              });
              
              TESTING && console.log(`Found ${bodyParts.length} body parts and ${accessories.length} accessories`);
              
              // Calculate bounding box based on body parts only (ignore large accessories)
              let normalizedCenter: THREE.Vector3;
              let normalizedSize: THREE.Vector3;
              
              if (bodyParts.length > 0) {
                // Use only body parts for positioning calculations
                const bodyBox = new THREE.Box3();
                bodyParts.forEach(mesh => {
                  const meshBox = new THREE.Box3().setFromObject(mesh);
                  bodyBox.union(meshBox);
                });
                normalizedCenter = bodyBox.getCenter(new THREE.Vector3());
                normalizedSize = bodyBox.getSize(new THREE.Vector3());
                TESTING && console.log('Using body-only bounding box for positioning');
              } else {
                // Fallback to full object if no body parts identified
                const fullBox = new THREE.Box3().setFromObject(object);
                normalizedCenter = fullBox.getCenter(new THREE.Vector3());
                normalizedSize = fullBox.getSize(new THREE.Vector3());
                TESTING && console.log('Using full object bounding box (no body parts identified)');
              }
              
              // Simple approach: just position the model at origin and scale appropriately
              object.position.set(0, 0, 0);
              
              // Rotate the model 180 degrees so it faces forward (Roblox models default to facing away)
              object.rotation.y = Math.PI;
              
              // Calculate scale to fit nicely in view
              const maxDim = Math.max(normalizedSize.x, normalizedSize.y, normalizedSize.z);
              const targetSize = 3; // Keep original size
              const scale = maxDim > 0 ? targetSize / maxDim : 1;
              object.scale.setScalar(scale);
              
              // Position the object based on view mode
              const worldCenter = normalizedCenter.clone().multiplyScalar(scale);
              
              if (viewMode === 'headshot') {
                // For headshot, position model lower so head is centered in view
                object.position.set(-worldCenter.x, -worldCenter.y - 1.2, -worldCenter.z);
              } else {
                // For full body, position model slightly lower to fit better in rectangular frame
                object.position.set(-worldCenter.x, -worldCenter.y - 0.3, -worldCenter.z);
              }
              
              TESTING && console.log(`View mode: ${viewMode}`);
              TESTING && console.log(`Normalized positioning - Center: (${normalizedCenter.x.toFixed(2)}, ${normalizedCenter.y.toFixed(2)}, ${normalizedCenter.z.toFixed(2)})`);
              TESTING && console.log(`Normalized size: (${normalizedSize.x.toFixed(2)}, ${normalizedSize.y.toFixed(2)}, ${normalizedSize.z.toFixed(2)})`);
              TESTING && console.log(`Final object position: (${object.position.x.toFixed(2)}, ${object.position.y.toFixed(2)}, ${object.position.z.toFixed(2)})`);
              TESTING && console.log(`Applied scale: ${scale.toFixed(3)} (target size: ${targetSize})`);
              
              // Ensure all meshes have proper materials
              object.traverse((child: any) => {
                if (child instanceof THREE.Mesh) {
                  child.visible = true;
                  
                  if (child.material) {
                    // Keep original material but ensure it's visible
                    if (Array.isArray(child.material)) {
                      child.material.forEach((mat: any) => {
                        mat.transparent = false;
                        mat.side = THREE.DoubleSide;
                      });
                    } else {
                      child.material.transparent = false;
                      child.material.side = THREE.DoubleSide;
                    }
                  } else {
                    // Fallback material
                    child.material = new THREE.MeshBasicMaterial({ 
                      color: 0xcccccc,
                      side: THREE.DoubleSide
                    });
                  }
                  
                  // Ensure mesh is not scaled to zero
                  if (child.scale.x === 0) child.scale.x = 1;
                  if (child.scale.y === 0) child.scale.y = 1;
                  if (child.scale.z === 0) child.scale.z = 1;
                }
              });
              
              scene.add(object);
              
              // Store model reference for scroll rotation
              if (sceneRef.current) {
                sceneRef.current.model = object;
              }
              
              TESTING && console.log('Model added to scene, total scene children:', scene.children.length);
              
              // Force immediate render to show the model
              renderer.render(scene, camera);
              TESTING && console.log('Initial render completed');
              
              // Start animation if enabled and not using scroll rotation
              if (autoRotate && !scrollRotation) {
                const animate = () => {
                  if (sceneRef.current) {
                    object.rotation.y += rotationSpeed;
                    
                    // Update controls if they exist
                    if (sceneRef.current.controls) {
                      sceneRef.current.controls.update();
                    }
                    
                    renderer.render(scene, camera);
                    sceneRef.current.animationId = requestAnimationFrame(animate);
                  }
                };
                TESTING && console.log(`Starting animation loop with rotation speed: ${rotationSpeed}`);
                animate();
              } else {
                // For static mode, still need to update controls if they exist
                if (sceneRef.current?.controls) {
                  const staticAnimate = () => {
                    if (sceneRef.current?.controls) {
                      sceneRef.current.controls.update();
                      renderer.render(scene, camera);
                      sceneRef.current.animationId = requestAnimationFrame(staticAnimate);
                    }
                  };
                  staticAnimate();
                  TESTING && console.log('Static render mode with controls enabled');
                } else {
                  TESTING && console.log('Static render mode (no animation, no controls)');
                }
              }
              
              resolve(object);
            },
            undefined,
            (error: any) => {
              reject(new Error(`Failed to load OBJ: ${error}`));
            }
          );
        },
        undefined,
        (error: any) => {
          reject(new Error(`Failed to load MTL: ${error}`));
        }
      );
    });
  }, [getCdnUrl, autoRotate, rotationSpeed, viewMode, scrollRotation]);

  // Main effect to load avatar
  useEffect(() => {
    if (!userId) return;

    const loadAvatar = async () => {
      try {
        setLoading(true);
        setError('');

        // Validate userId is numeric
        if (!/^\d+$/.test(userId.trim())) {
          throw new Error('User ID must be numbers only');
        }

        // Fetch avatar data
        const data = await fetchAvatarData(userId.trim());
        
        // Check if avatar is ready
        if (!data.ready) {
          // If not ready and we haven't retried too many times, retry after a delay
          if (retryCount < 3) {
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
            }, 2000); // Retry after 2 seconds
            return;
          } else {
            throw new Error(data.message ?? 'Avatar not ready after multiple attempts');
          }
        }
        
        setAvatarData(data);

        // Setup Three.js scene
        const sceneData = setupScene();
        if (!sceneData) {
          throw new Error('Failed to setup 3D scene');
        }

        sceneRef.current = sceneData;

        // Load the model
        await loadModel(sceneData, data);
        
        // Clear any previous errors since we successfully loaded
        setError('');
        onLoad?.();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadAvatar();

    // Cleanup
    return () => {
      const currentSceneRef = sceneRef.current;
      const currentContainerRef = containerRef.current;
      
      if (currentSceneRef) {
        if (currentSceneRef.animationId) {
          cancelAnimationFrame(currentSceneRef.animationId);
        }
        if (currentSceneRef.controls) {
          currentSceneRef.controls.dispose();
        }
        if (currentContainerRef && currentSceneRef.renderer.domElement && 
            currentContainerRef.contains(currentSceneRef.renderer.domElement)) {
          currentContainerRef.removeChild(currentSceneRef.renderer.domElement);
        }
        currentSceneRef.renderer.dispose();
        sceneRef.current = null;
      }
    };
  }, [userId, width, height, backgroundColor, autoRotate, rotationSpeed, viewMode, retryCount, loadModel, onError, onLoad, setupScene, fetchAvatarData]);

  return (
    <Box
      className={className}
      style={{
        width,
        height,
        position: 'relative',
        borderRadius: '8px',
        overflow: 'hidden',
        ...style
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
        }}
      />
      
      {/* Loading overlay */}
      {loading && showLoading && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            zIndex: 10
          }}
        >
          <Loader size="md" color="white" />
          <Text size="sm" mt="xs">Loading 3D Avatar...</Text>
        </Box>
      )}
      
      {/* Error overlay */}
      {error && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            textAlign: 'center',
            padding: '16px',
            zIndex: 10
          }}
        >
          <Text size="sm" color="red">Failed to load 3D avatar</Text>
          <Text size="xs" mt="xs" opacity={0.8}>{error}</Text>
        </Box>
      )}
    </Box>
  );
};

export default RobloxAvatar3D; 